import { assign, createMachine } from "xstate";
import { IOrchestrators } from "@nju/data-access-portal-orchestrator";
import {
  deleteChangeSimEsim,
  getEsimQRCode,
  getSimCardList,
  IEsimQRCode,
  IPukNumber,
} from "@nju/data-access-portal-sim-card";

interface IContext {
  pukNumber?: IPukNumber;
  simNumber: string | undefined;
  userId: string | undefined;
  iccId: string;
  subState: IEsimQRCode["subState"] | undefined;
  changeSimEsim: IOrchestrators["changeSimEsim"];
  isEsimProducts: boolean;
}

type IEvent =
  | { type: "ERROR" }
  | {
      type: "SET_DATA";
      pukNumber: IPukNumber | undefined;
      subState: IEsimQRCode["subState"] | undefined;
    }
  | { type: "DELETE_CHANGE_SIM_ESIM" }
  | { type: "DELETE_CHANGE_SIM_ESIM_SUCCESS" }
  | { type: "DELETE_CHANGE_SIM_ESIM_ERROR" };

export const mySimCardPageMachine = createMachine(
  {
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    initial: "loading",
    states: {
      loading: {
        invoke: {
          src: "fetchData",
        },
        on: {
          SET_DATA: {
            actions: "setData",
            target: "ready",
          },
          ERROR: {
            target: "error",
          },
        },
      },
      ready: {
        on: {
          DELETE_CHANGE_SIM_ESIM: {
            target: "deleteChangeSimEsim",
          },
        },
      },
      deleteChangeSimEsim: {
        invoke: {
          src: "deleteChangeSimEsim",
        },
        on: {
          DELETE_CHANGE_SIM_ESIM_SUCCESS: {
            target: "finish",
          },
          DELETE_CHANGE_SIM_ESIM_ERROR: {
            target: "error",
          },
        },
      },
      error: {
        type: "final",
      },
      finish: {
        type: "final",
      },
    },
  },
  {
    actions: {
      setData: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_DATA") {
          return {
            pukNumber: event.pukNumber,
            subState: event.subState,
          };
        }

        return context;
      }),
    },
    services: {
      fetchData: ({
        simNumber,
        userId,
        iccId,
        changeSimEsim,
        isEsimProducts,
      }) => {
        return async (callback) => {
          try {
            const simCardResult = (
              await getSimCardList({ simNumber, userId })
            ).unwrap();

            const esimsResult =
              changeSimEsim?.deliveryMethodId === "ESIM" || isEsimProducts
                ? (await getEsimQRCode({ iccId, userId })).unwrap()
                : undefined;

            return callback({
              type: "SET_DATA",
              pukNumber: simCardResult.pukNumber,
              subState: esimsResult?.eSim?.subState,
            });
          } catch {
            callback({ type: "ERROR" });
          }
        };
      },
      deleteChangeSimEsim: ({ changeSimEsim }) => {
        return async (callback) => {
          if (!changeSimEsim) {
            return callback({ type: "DELETE_CHANGE_SIM_ESIM_ERROR" });
          }

          const result = await deleteChangeSimEsim({
            orchestratorId: changeSimEsim?.id,
          });

          if (result.isError) {
            return callback({ type: "DELETE_CHANGE_SIM_ESIM_ERROR" });
          }

          callback({ type: "DELETE_CHANGE_SIM_ESIM_SUCCESS" });
        };
      },
    },
  },
);
